/**
 * The one definition of what the widget tells a customer when their request was saved but the
 * CRM forward failed.
 *
 * It lives here, alone, because the sentence was written twice - once in the API client and
 * once in the confirmation view - and two copies of a claim are two chances for one of them to
 * keep asserting something that stopped being true. The first version said "they have been
 * notified", and nothing notifies anyone: the quote row is written, and that is the whole of
 * what happens.
 *
 * Rules for anything written here:
 *  - never imply the request reached the CRM;
 *  - never promise an action nothing performs (a notification, an email, a callback);
 *  - no plan or tier words - a customer is not a subscriber and it is not their business.
 */
/**
 * Whether the CRM leg of a submission is known NOT to have arrived, and the confirmation
 * screen must therefore not celebrate a delivery.
 *
 * `failed` and `pending` both mean the request has not reached the CRM - pending is a forward
 * that was never attempted or never recorded, which is no more delivered than a failure.
 * `skipped` is a tenant with no CRM configured, where nothing was ever meant to arrive, and
 * `undefined` is a response carrying no CRM fields at all (an older mock, or the anti-spam
 * fake-success response, which must stay indistinguishable from a real one). Both of those
 * keep the ordinary success screen.
 */
export function isCrmSyncUnconfirmed(
  crmSync: "forwarded" | "failed" | "skipped" | "pending" | undefined
): boolean {
  return crmSync === "failed" || crmSync === "pending";
}

export function crmSyncFailedMessage(hasReference: boolean): string {
  return hasReference
    ? "We saved your request, but it has not reached our team's system yet. " +
        "Please keep the reference below; our team will use it to follow up."
    : "We saved your request, but it has not reached our team's system yet. " +
        "Our team will use it to follow up.";
}
